import { lazy, Suspense, useEffect, useState } from "react";
import { Switch, Route, Redirect, Router as WouterRouter, useLocation } from "wouter";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { PillToaster } from "@/components/calendar/pill-toast";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ArrowUp } from "lucide-react";
import NotFound from "@/pages/not-found";
import Landing from "@/pages/landing";
import InputScreen from "@/pages/input";
import ProcessingScreen from "@/pages/processing";
import ReflectScreen from "@/pages/reflect";
import JournalScreen from "@/pages/journal";
import LibraryScreen from "@/pages/library";
import HomeScreen from "@/pages/home";
import JournalsScreen from "@/pages/journals";
import MomentumScreen from "@/pages/momentum";
import PlanStepScreen from "@/pages/plan-step";
import PricingScreen from "@/pages/pricing";
import SettingsScreen from "@/pages/settings";
import Onboarding from "@/pages/onboarding";
import JournalPreview from "@/pages/journal-preview";
import JournalSharePage from "@/pages/journal-share";
import LoginPage from "@/pages/login";
import ContactPage from "@/pages/contact";
import PrivacyPage from "@/pages/privacy";
import TermsPage from "@/pages/terms";
import ResetPasswordPage from "@/pages/reset-password";
import AuthCallbackPage from "@/pages/auth-callback";
import BillingSuccessPage from "@/pages/billing-success";
import AssistantConsentPage from "@/pages/assistant-consent";
import { AssistantActivityHost } from "@/components/assistant/assistant-activity";
import { AuthProvider } from "@/contexts/auth-context";
import { useAuth } from "@/hooks/useAuth";
import {
  getAppUrl,
  getMarketingUrl,
  isCrossDomainConfigured,
  isOnAppDomain,
  isOnMarketingDomain,
} from "@/lib/auth-redirect";
import { clearAuthFlag, hasAuthFlag } from "@/lib/auth-flag";
import { readPendingInput } from "@/lib/pending-input";
import { takeUpgradeIntent } from "@/lib/upgrade-intent";

/** Dev-only stand-in for Claude and ChatGPT that drives the MCP connector (never in production builds). */
const AssistantSimulator = import.meta.env.DEV ? lazy(() => import("@/components/assistant/dev/assistant-simulator")) : null;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const PUBLIC_PATHS = ["/", "/features", "/explore", "/faq", "/pricing", "/journal-preview", "/login", "/reset-password", "/auth/callback", "/contact", "/privacy", "/terms"];

/** Shared journal links (`/share/journal/:id`) are public regardless of login state — the
 * dynamic id segment means they can't live in the flat PUBLIC_PATHS list above. */
function isPublicSharePath(pathname: string): boolean {
  return pathname.startsWith("/share/journal/");
}

function normalizePathname(pathname: string): string {
  if (!pathname) return "/";
  const normalized = pathname.replace(/\/+$/, "");
  return normalized || "/";
}

function LandingSectionRoute({ sectionId }: { sectionId: string }) {
  useEffect(() => {
    const scrollToSection = () => {
      const section = document.getElementById(sectionId);
      if (!section) return;
      section.scrollIntoView({ behavior: "auto", block: "start" });
    };

    // Ensure section exists after landing renders.
    const raf = requestAnimationFrame(scrollToSection);
    return () => cancelAnimationFrame(raf);
  }, [sectionId]);

  return <Landing />;
}

function FeaturesPage() {
  return <LandingSectionRoute sectionId="features" />;
}

function ExplorePage() {
  return <LandingSectionRoute sectionId="explore" />;
}

function FaqPage() {
  return <LandingSectionRoute sectionId="faq" />;
}

function RouteScrollManager() {
  const [location] = useLocation();

  useEffect(() => {
    // Section routes have explicit scrolling handled by their own wrappers.
    if (location === "/features" || location === "/explore" || location === "/faq") {
      return;
    }
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location]);

  return null;
}

function ScrollToTopButton() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setVisible(window.scrollY > 320);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (!visible) return null;

  return (
    <button
      type="button"
      aria-label="Scroll to top"
      onClick={() => window.scrollTo({ top: 0, left: 0, behavior: "smooth" })}
      className="fixed bottom-6 right-5 z-[60] w-11 h-11 rounded-full bg-foreground text-background shadow-lg hover:bg-foreground/90 transition-all hidden md:flex items-center justify-center"
    >
      <ArrowUp className="w-5 h-5" />
    </button>
  );
}

function PendingInputGate() {
  const [, setLocation] = useLocation();
  const [hasPending] = useState(() => Boolean(readPendingInput()));
  useEffect(() => {
    if (hasPending) setLocation("/app", { replace: true });
  }, [hasPending, setLocation]);
  if (hasPending) {
    return (
      <div className="min-h-screen bg-[hsl(40_30%_97%)] flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-[hsl(258_35%_50%)] border-t-transparent animate-spin" />
      </div>
    );
  }
  return <HomeScreen />;
}

function AuthedRoutes() {
  const { user, isLoading, isAuthenticated, login, refetch } = useAuth();
  const [location] = useLocation();
  const pathname = normalizePathname(location.split("?")[0] ?? "/");

  // Recovery links can trigger several auth state transitions (code exchange, token updates).
  // Keep reset page outside global auth gating to avoid visual reload/flicker loops.
  if (pathname === "/reset-password") {
    return <ResetPasswordPage />;
  }

  // OAuth callback exchanges a one-time code — keep outside auth gating to avoid flicker.
  if (pathname === "/auth/callback") {
    return <AuthCallbackPage />;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[hsl(40_30%_97%)] flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-[hsl(258_35%_50%)] border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    // Always allow auth-flow paths so OAuth/email links can complete on the app domain.
    const AUTH_FLOW_PATHS = new Set(["/login", "/auth/callback", "/reset-password"]);
    if (AUTH_FLOW_PATHS.has(pathname)) {
      return (
        <Switch>
          <Route path="/login" component={LoginPage} />
          <Route path="/reset-password" component={ResetPasswordPage} />
          <Route path="/auth/callback" component={AuthCallbackPage} />
        </Switch>
      );
    }

    // Split-domain prod: bounce unauthenticated visitors on the app subdomain to marketing —
    // except shared journal links, which must render on whichever domain they were opened
    // from rather than bounce a WhatsApp/etc. recipient with no account to the homepage.
    if (isCrossDomainConfigured() && isOnAppDomain() && !isPublicSharePath(pathname)) {
      // Drop the cross-subdomain flag first so the marketing bouncer doesn't
      // immediately send us back here (would loop). The auth context also
      // clears it on null-session sync, but that runs async — clear inline.
      clearAuthFlag();
      window.location.replace(getMarketingUrl());
      return (
        <div className="min-h-screen bg-[hsl(40_30%_97%)] flex items-center justify-center">
          <div className="w-8 h-8 rounded-full border-2 border-[hsl(258_35%_50%)] border-t-transparent animate-spin" />
        </div>
      );
    }

    if (PUBLIC_PATHS.includes(pathname) || isPublicSharePath(pathname)) {
      return (
        <Switch>
          <Route path="/features" component={FeaturesPage} />
          <Route path="/explore" component={ExplorePage} />
          <Route path="/faq" component={FaqPage} />
          <Route path="/" component={Landing} />
          <Route path="/pricing" component={PricingScreen} />
          <Route path="/journal-preview" component={JournalPreview} />
          <Route path="/share/journal/:id" component={JournalSharePage} />
          <Route path="/login" component={LoginPage} />
          <Route path="/reset-password" component={ResetPasswordPage} />
          <Route path="/auth/callback" component={AuthCallbackPage} />
          <Route path="/contact" component={ContactPage} />
          <Route path="/privacy" component={PrivacyPage} />
          <Route path="/terms" component={TermsPage} />
          <Route component={Landing} />
        </Switch>
      );
    }
    login();
    return (
      <div className="min-h-screen bg-[hsl(40_30%_97%)] flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-[hsl(258_35%_50%)] border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!user!.onboardingCompleted) {
    return <Onboarding user={user!} onComplete={refetch} />;
  }

  return (
    <>
    <UpgradeIntentResume />
    <Switch>
      <Route path="/features" component={FeaturesPage} />
      <Route path="/explore" component={ExplorePage} />
      <Route path="/faq" component={FaqPage} />
      <Route path="/login" component={LoginPage} />
      <Route path="/" component={PendingInputGate} />
      <Route path="/app" component={InputScreen} />
      <Route path="/processing/:sessionId" component={ProcessingScreen} />
      <Route path="/reflect/:sessionId" component={ReflectScreen} />
      <Route path="/journal/:id" component={JournalScreen} />
      <Route path="/share/journal/:id" component={JournalSharePage} />
      <Route path="/library" component={LibraryScreen} />
      <Route path="/unfoldings" component={JournalsScreen} />
      <Route path="/momentum/plan/:journalId/:actionId" component={PlanStepScreen} />
      <Route path="/momentum" component={MomentumScreen} />
      <Route path="/journals"><Redirect to="/unfoldings" /></Route>
      <Route path="/pricing" component={PricingScreen} />
      <Route path="/billing/success" component={BillingSuccessPage} />
      <Route path="/oauth/consent" component={AssistantConsentPage} />
      {AssistantSimulator && (
        <Route path="/dev/assistant">
          <Suspense fallback={null}>
            <AssistantSimulator />
          </Suspense>
        </Route>
      )}
      <Route path="/settings" component={SettingsScreen} />
      <Route path="/reset-password" component={ResetPasswordPage} />
      <Route path="/auth/callback" component={AuthCallbackPage} />
      <Route path="/contact" component={ContactPage} />
      <Route path="/privacy" component={PrivacyPage} />
      <Route path="/terms" component={TermsPage} />
      <Route component={NotFound} />
    </Switch>
    </>
  );
}

/**
 * Signed-in users who clicked "Upgrade" while signed out: sign-in (password or Google)
 * always lands on `/`, so pick the remembered plan up here and continue to the pricing
 * page, which starts checkout. Runs once per remembered intent.
 */
function UpgradeIntentResume() {
  const [location, setLocation] = useLocation();
  useEffect(() => {
    const plan = takeUpgradeIntent();
    if (plan && !location.startsWith("/pricing")) setLocation(`/pricing?upgrade=${plan}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

/**
 * Marketing → app bouncer.
 *
 * If a user is on the marketing origin (unfoldwin.com) and our cross-subdomain
 * flag says they have a session living on app.unfoldwin.com, immediately send
 * them to the app. The app domain is the source of truth: if it later finds
 * the session is gone, it clears the flag, so this won't loop.
 *
 * Skipped for any auth-flow path so password reset / oauth callback links
 * still work even if someone clicks one while logged in.
 */
function MarketingAuthBouncer() {
  useEffect(() => {
    if (!isCrossDomainConfigured()) return;
    if (!isOnMarketingDomain()) return;
    if (!hasAuthFlag()) return;
    const path = window.location.pathname;
    if (path.startsWith("/auth/") || path === "/reset-password" || path === "/login") return;
    window.location.replace(`${getAppUrl()}/`);
  }, []);
  return null;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
          <AuthProvider>
            <MarketingAuthBouncer />
            <RouteScrollManager />
            <AuthedRoutes />
            <AssistantActivityHost />
            <ScrollToTopButton />
          </AuthProvider>
        </WouterRouter>
        <Toaster />
        <PillToaster />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
